import React from 'react';
import { useLanguage } from '../../context/LanguageContext';

// African countries SVG paths - simplified representation
const africanCountries = [
  { name: 'Centrafrique', code: 'CF', status: 'member', cx: 52, cy: 45 },
  { name: 'Mali', code: 'ML', status: 'member', cx: 32, cy: 35 },
  { name: 'Burkina Faso', code: 'BF', status: 'member', cx: 35, cy: 40 },
  { name: 'Niger', code: 'NE', status: 'observer', cx: 42, cy: 35 },
  { name: 'Cameroun', code: 'CM', status: 'member', cx: 48, cy: 48 },
  { name: 'Tchad', code: 'TD', status: 'observer', cx: 52, cy: 38 },
  { name: 'Sénégal', code: 'SN', status: 'candidate', cx: 22, cy: 40 },
  { name: 'Guinée', code: 'GN', status: 'candidate', cx: 25, cy: 45 },
  { name: 'RDC', code: 'CD', status: 'observer', cx: 55, cy: 55 },
  { name: 'Éthiopie', code: 'ET', status: 'candidate', cx: 70, cy: 45 },
  { name: 'Soudan', code: 'SD', status: 'observer', cx: 62, cy: 35 },
  { name: 'Égypte', code: 'EG', status: 'candidate', cx: 60, cy: 25 },
];

export const MapSection = () => {
  const { t } = useLanguage();

  const getStatusColor = (status) => {
    switch (status) {
      case 'member':
        return '#D52B1E'; // Russian red
      case 'observer':
        return '#0039A6'; // Russian blue
      case 'candidate':
        return '#228B22'; // African green
      default:
        return '#E2E8F0';
    }
  };

  return (
    <section
      data-testid="map-section"
      className="py-24 md:py-32 bg-white relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div>
            <span className="text-xs md:text-sm uppercase tracking-[0.2em] text-oar-orange font-bold">
              Présence Continentale
            </span>
            <h2
              data-testid="map-title"
              className="font-heading text-3xl md:text-5xl font-semibold tracking-tight text-oar-text-primary mt-4 mb-6"
            >
              {t('map.title')}
            </h2>
            <p className="text-base md:text-lg text-oar-text-secondary leading-relaxed mb-8">
              {t('map.description')}
            </p>

            {/* Legend */}
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-oar-red" />
                <span className="text-sm text-oar-text-secondary">{t('map.legend.member')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-oar-blue" />
                <span className="text-sm text-oar-text-secondary">{t('map.legend.observer')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-oar-green" />
                <span className="text-sm text-oar-text-secondary">{t('map.legend.candidate')}</span>
              </div>
            </div>
          </div>

          {/* Map SVG */}
          <div className="relative">
            <div className="bg-oar-bg-secondary rounded-lg p-8 shadow-inner">
              <svg
                data-testid="africa-map"
                viewBox="0 0 100 100"
                className="w-full max-w-lg mx-auto"
              >
                {/* Africa continent outline - simplified */}
                <path
                  d="M30 15 L45 10 L60 12 L75 20 L80 35 L78 50 L72 60 L65 75 L55 85 L45 88 L35 82 L28 70 L22 55 L20 40 L25 25 Z"
                  fill="#E2E8F0"
                  stroke="#CBD5E0"
                  strokeWidth="0.5"
                />

                {/* Country markers */}
                {africanCountries.map((country) => (
                  <g key={country.code} className="cursor-pointer">
                    <circle
                      cx={country.cx}
                      cy={country.cy}
                      r="3"
                      fill={getStatusColor(country.status)}
                      className="transition-all duration-300"
                    >
                      <animate
                        attributeName="opacity"
                        values="0.7;1;0.7"
                        dur="2s"
                        repeatCount="indefinite"
                      />
                    </circle>
                    <circle
                      cx={country.cx}
                      cy={country.cy}
                      r="5"
                      fill="none"
                      stroke={getStatusColor(country.status)}
                      strokeWidth="0.5"
                      opacity="0.3"
                    />
                  </g>
                ))}

                {/* Connection lines to Russia (top right) */}
                <line
                  x1="52"
                  y1="45"
                  x2="85"
                  y2="5"
                  stroke="#0039A6"
                  strokeWidth="0.5"
                  strokeDasharray="2,2"
                  opacity="0.6"
                />
                <circle cx="85" cy="5" r="2" fill="#0039A6" opacity="0.8" />
                <text x="87" y="6" fill="#0039A6" fontSize="3" fontFamily="Manrope" fontWeight="bold">
                  RU
                </text>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
